import { FC, useState } from "react";
import { Control, useWatch } from "react-hook-form";
import {
  FormController,
  MuiBox,
  MuiGrid,
  MuiLink,
  MuiSelect,
  MuiTextField,
} from "../../../../../../../../../../../../components";
import { ManageLocale } from "../../../../../../../../../../../../components/manageLocale/ManageLocale";
import MeasurementInput from "../../../../../../../../../../../../components/measurementInput/MeasurementInput";
import { MAX_LENGTH_75 } from "../../../../../../../../../../../../helpers/constants";
import { KeyValue } from "../../../../../../../../../../../../helpers/types/common";
import { useGridContainer } from "../../../../../../../../../../../../hooks/useGridContainer";
import { useNavigationSiteCore } from "../../../../../../../../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../../../../../../../../hooks/useSiteCore";
import {
  ProjectCreateDeliverablesFormKeys,
  ProjectCreateDeliverablesFormProps,
} from "../../../../modelsAndSchemas/models";
import { ProjectNewDeliverableWrapper } from "./ProjectNewDeliverable.style";
import { addDeliverableSiteCoreKeys } from "../../../../siteCore/SiteCoreKeys";

export interface ProjectNewDeliverableProps {
  pageId: string;
  selectedUnit: string;
  control: Control<ProjectCreateDeliverablesFormProps>;
  onUnitValueChange: (unit: string) => void;
  manageLocaleTitle: string;
  manageLocaleInfoMessage: string;
  onClickSaveManageLocal: (localeData: KeyValue) => void;
  userSelectedLanguage: string;
}

const ProjectNewDeliverable: FC<ProjectNewDeliverableProps> = ({
  pageId,
  selectedUnit,
  control,
  onUnitValueChange,
  manageLocaleTitle,
  manageLocaleInfoMessage,
  userSelectedLanguage,
  onClickSaveManageLocal,
}) => {
  const { commonLabels, fetchLabel, fetchDropdownValues } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();

  const [showManageLocale, setShowManageLocale] = useState(false);

  const { gridSizeMedium: gridSize, gridSizeMediumClass } =
    useGridContainer(true);

  const handleShowManageLocale = () => {
    setShowManageLocale(true);
  };

  const handleCloseShowManageLocale = () => {
    setShowManageLocale(false);
  };

  const deliverablesNames = useWatch({
    name: ProjectCreateDeliverablesFormKeys.name,
    control,
  });

  return (
    <>
      <ProjectNewDeliverableWrapper>
        <MuiGrid container className="form-container">
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={ProjectCreateDeliverablesFormKeys.deliverableType}
            >
              <MuiSelect
                label={fetchLabel(
                  addDeliverableSiteCoreKeys.addDeliverable,
                  addDeliverableSiteCoreKeys.deliverableType,
                )}
                options={fetchDropdownValues(
                  addDeliverableSiteCoreKeys.addDeliverable,
                  addDeliverableSiteCoreKeys.deliverableTypeList,
                )}
                isMandatory
                value=""
                visibleDefaultSelectOption
                id={`${pageId}-${ProjectCreateDeliverablesFormKeys.deliverableType}`}
                placeholder={commonLabels.selectPlaceHolder}
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiGrid>

          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <MuiBox className="deliverable-name">
              <FormController
                control={control}
                name={`${ProjectCreateDeliverablesFormKeys.name}.${userSelectedLanguage}`}
              >
                <MuiTextField
                  label={fetchLabel(
                    addDeliverableSiteCoreKeys.addDeliverable,
                    addDeliverableSiteCoreKeys.deliverableName,
                  )}
                  placeholder={commonLabels.inputPlaceHolder}
                  id={`${pageId}-${ProjectCreateDeliverablesFormKeys.name}`}
                  isMandatory
                  maxLength={MAX_LENGTH_75}
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
              <MuiLink
                className="manage-locale-link"
                id={`${pageId}-locale`}
                data-testid={`${pageId}-locale`}
                onClick={handleShowManageLocale}
              >
                {commonLabels.locale}
              </MuiLink>
            </MuiBox>
          </MuiGrid>
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={ProjectCreateDeliverablesFormKeys.units}
            >
              <MuiSelect
                label={fetchLabel(
                  addDeliverableSiteCoreKeys.addDeliverable,
                  addDeliverableSiteCoreKeys.units,
                )}
                options={fetchDropdownValues(
                  addDeliverableSiteCoreKeys.addDeliverable,
                  addDeliverableSiteCoreKeys.unitsList,
                )}
                visibleDefaultSelectOption
                value=""
                id={`${pageId}-${ProjectCreateDeliverablesFormKeys.units}`}
                isMandatory
                placeholder={commonLabels.selectPlaceHolder}
                onChange={(e: any) =>
                  onUnitValueChange(e.target.value as string)
                }
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiGrid>
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <MeasurementInput
              selectedUnit={selectedUnit}
              measurementOtherMinuteName={
                ProjectCreateDeliverablesFormKeys.measurementMinute
              }
              measurementOther={
                ProjectCreateDeliverablesFormKeys.measurementOther
              }
              control={control}
              pageId={pageId}
              parentKey={addDeliverableSiteCoreKeys.addDeliverable}
              minutesLabelKey={addDeliverableSiteCoreKeys.minutes}
              quantityLabelKey={addDeliverableSiteCoreKeys.quantity}
              daysLabelKey={addDeliverableSiteCoreKeys.numberOfDays}
              minuteListKey={addDeliverableSiteCoreKeys.minutesList}
            />
          </MuiGrid>
        </MuiGrid>
      </ProjectNewDeliverableWrapper>
      {showManageLocale && (
        <ManageLocale
          localNameTitle={fetchLabel(
            addDeliverableSiteCoreKeys.addDeliverable,
            addDeliverableSiteCoreKeys.deliverableName,
          )}
          show={showManageLocale}
          handleCloseModal={handleCloseShowManageLocale}
          localeLanguages={deliverablesNames}
          onSaveManageLocalCallback={onClickSaveManageLocal}
          title={manageLocaleTitle}
          maxLength={MAX_LENGTH_75}
          bannerInfoMessage={manageLocaleInfoMessage}
        />
      )}
    </>
  );
};

export default ProjectNewDeliverable;
